<!--
  Order details widget — rewritten 2026-06-09.

  Previously a kendo-dialog + kendo-formfield form + kendo-panelbar
  accordion. Now plain HTML rendered inline inside the parent modal
  (agente-list or client-list owns the modal chrome). Visual language
  matches the Diakonix family — sections with title, dt/dd grid for
  fields, striped table for products.

  Three sections:
    1. "Ordine"     — basic identifiers + status + DDT
    2. "Spedizione" — customer + full address + carrier
    3. "Prodotti"   — table with `📜` history-trigger button per row
-->

@if (loading) {

  <!-- ─── Loading skeleton ───────────────────────────────────────── -->
  <div class="dettagli-skeleton" aria-busy="true">
    <div class="dettagli-skeleton__title"></div>
    @for (i of [1, 2, 3]; track i) {
      <div class="dettagli-skeleton__section">
        <div class="dettagli-skeleton__row dettagli-skeleton__row--lg"></div>
        <div class="dettagli-skeleton__row dettagli-skeleton__row--md"></div>
        <div class="dettagli-skeleton__row dettagli-skeleton__row--sm"></div>
      </div>
    }
  </div>

} @else if (!order && !orderNew) {

  <!-- ─── Empty / load-failed state ──────────────────────────────── -->
  <div class="dettagli-empty">
    <div class="dettagli-empty__icon" aria-hidden="true">📭</div>
    <p class="dettagli-empty__msg">
      Impossibile caricare i dettagli di questo ordine.
    </p>
  </div>

} @else {

  <article class="dettagli">

    <!-- ─── Section 1 — Ordine ─────────────────────────────────── -->
    <section class="dettagli-section" aria-label="Dati ordine">
      <header class="dettagli-section__header">
        <h3 class="dettagli-section__title">📦 Ordine</h3>
        <span class="dettagli-section__status" [attr.data-state]="isNewOrder ? 'pending' : 'assigned'">
          {{ statusText }}
        </span>
      </header>

      <dl class="dettagli-grid">
        <div class="dettagli-grid__row">
          <dt>N° ordine</dt>
          <dd class="dettagli-grid__val--accent">{{ orderNumber }}</dd>
        </div>
        <div class="dettagli-grid__row">
          <dt>Data ordine</dt>
          <dd>{{ orderDate }}</dd>
        </div>
        <div class="dettagli-grid__row">
          <dt>Data evasione</dt>
          <dd>{{ closingDate }}</dd>
        </div>
        @if (!isNewOrder) {
          <div class="dettagli-grid__row">
            <dt>Operatore</dt>
            <dd>{{ operator }}</dd>
          </div>
          <div class="dettagli-grid__row dettagli-grid__row--full">
            <dt>DDT</dt>
            <dd class="dettagli-grid__val--ddt">
              <span class="ddt-number">{{ ddtNumber }}</span>
              @if (hasDdtPdf) {
                <button
                  type="button"
                  class="ddt-btn ddt-btn--open"
                  (click)="openDdt()"
                  title="Apri DDT PDF"
                >📄 Apri PDF</button>
              }
              <button
                type="button"
                class="ddt-btn ddt-btn--regen"
                (click)="printDdt()"
                title="Rigenera DDT con la stampante selezionata"
              >🖨️ Rigenera</button>
            </dd>
          </div>
        }
      </dl>
    </section>

    <!-- ─── Section 2 — Spedizione ────────────────────────────── -->
    <section class="dettagli-section" aria-label="Dati spedizione">
      <header class="dettagli-section__header">
        <h3 class="dettagli-section__title">🚚 Spedizione</h3>
      </header>

      <dl class="dettagli-grid">
        <div class="dettagli-grid__row dettagli-grid__row--full">
          <dt>Cliente</dt>
          <dd class="dettagli-grid__val--strong">{{ customer }}</dd>
        </div>
        <div class="dettagli-grid__row dettagli-grid__row--full">
          <dt>Indirizzo</dt>
          <dd>{{ shippingAddress }}</dd>
        </div>
        <div class="dettagli-grid__row">
          <dt>Città</dt>
          <dd>{{ shippingCity }}</dd>
        </div>
        <div class="dettagli-grid__row">
          <dt>CAP</dt>
          <dd>{{ shippingZip }}</dd>
        </div>
        <div class="dettagli-grid__row">
          <dt>Provincia</dt>
          <dd>{{ shippingProv }}</dd>
        </div>
        <div class="dettagli-grid__row">
          <dt>Nazione</dt>
          <dd>{{ shippingCountry }}</dd>
        </div>
        <div class="dettagli-grid__row dettagli-grid__row--full">
          <dt>Corriere</dt>
          <dd>{{ carrier }}</dd>
        </div>
      </dl>
    </section>

    <!-- ─── Section 3 — Prodotti ──────────────────────────────── -->
    <section class="dettagli-section" aria-label="Prodotti dell'ordine">
      <header class="dettagli-section__header">
        <h3 class="dettagli-section__title">🧱 Prodotti</h3>
        <span class="dettagli-section__count">
          {{ displayedProducts.length }}
          {{ displayedProducts.length === 1 ? 'articolo' : 'articoli' }}
        </span>
      </header>

      @if (displayedProducts.length === 0) {
        <p class="dettagli-products__empty">Nessun prodotto associato a questo ordine.</p>
      } @else {
        <ul class="dettagli-products" role="list">
          @for (p of displayedProducts; track p.code + p.description) {
            <li class="product-card">
              <span class="product-card__desc">{{ p.description }}</span>
              <span class="product-card__qty">{{ p.qty }} pz</span>
              @if (p.code) {
                <button
                  type="button"
                  class="product-card__history"
                  title="Storico prodotto"
                  (click)="onProductHistoryClick(p.code)"
                >📜</button>
              }
            </li>
          }
        </ul>
      }
    </section>

  </article>
}
